import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ToggleSwitch from "../../../ToggleSwitch";
import InfoTip from "./InfoTip";
import {
  formControlClass,
  formControlErrorClass,
  formErrorTextClass,
} from "../../../dashboardStyles";
import { resourceFieldId } from "../utils/validation";
import {
  DAY_KEYS,
  SCHEDULE_PRESETS,
  advancedFromSimple,
  describeSchedule,
  presetOf,
  workingDaysFor,
} from "../utils/schedule";

const PRESETS = ["weekdays", "weekend", "custom"];

const chipClass = (active) =>
  `inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border px-3 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    active
      ? "border-brand-600 bg-brand-50 text-brand-800"
      : "border-ink-200 bg-white text-ink-600 hover:border-brand-300"
  }`;

const dayClass = (active) =>
  `inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-lg border text-sm sm:w-11 font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    active
      ? "border-brand-600 bg-brand-600 text-white hover:bg-brand-700"
      : "border-ink-200 bg-white text-ink-500 hover:border-brand-600 hover:text-brand-700"
  }`;

/**
 * "When are you available?" — a resource's week, the simple way first.
 *
 * Two presets and the seven days cover almost everyone: the same hours on the
 * days ticked. "Different hours per day" sits behind a fold for the rest, and
 * once switched on it *is* the schedule (the API reads `advancedSchedule` and
 * ignores the simple days), so the simple controls step aside while it is on.
 *
 * Breaks inside a day are not here: the data model has nowhere to keep them
 * yet, and availability would not honour them.
 */
const AvailabilityEditor = ({ resource, errors = {}, onUpdate }) => {
  const { t } = useTranslation();
  // "Custom" chosen with days that happen to match a preset should still read
  // as Custom until the days change; otherwise the press appears to do nothing.
  const [presetOverride, setPresetOverride] = useState(null);
  const [perDayOpen, setPerDayOpen] = useState(false);

  const uid = resource.id;
  const daysId = resourceFieldId("days", uid);
  const hoursId = resourceFieldId("hours", uid);
  const daysError = errors[daysId];
  const hoursError = errors[hoursId];
  const advanced = Boolean(resource.useAdvancedSchedule);
  const workingDays = resource.workingDays || workingDaysFor([]);
  const preset = presetOverride ?? presetOf(workingDays);
  const isPerson = resource.type === "Person";

  const choosePreset = (value) => {
    if (value === "custom") {
      setPresetOverride("custom");
      return;
    }
    setPresetOverride(null);
    onUpdate("workingDays", workingDaysFor(SCHEDULE_PRESETS[value]));
  };

  const toggleDay = (day) => {
    setPresetOverride(null);
    onUpdate("workingDays", { ...workingDays, [day]: !workingDays[day] });
  };

  const setHours = (field, value) =>
    onUpdate("workingHours", { ...resource.workingHours, [field]: value });

  const setAdvanced = (on) => {
    if (on) {
      onUpdate(
        "advancedSchedule",
        advancedFromSimple(workingDays, resource.workingHours),
      );
    }
    onUpdate("useAdvancedSchedule", on);
  };

  const setDay = (day, field, value) =>
    onUpdate("advancedSchedule", {
      ...resource.advancedSchedule,
      [day]: { ...resource.advancedSchedule?.[day], [field]: value },
    });

  const dayName = (day) => t(`Dashboard.Setup.days.long.${day}`);
  const panelOpen = advanced || perDayOpen;
  const panelId = `per-day-${uid}`;

  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span id={`${daysId}-label`} className="text-sm font-semibold text-ink-900">
          {t(
            isPerson
              ? "Dashboard.Setup.team.whenPerson"
              : "Dashboard.Setup.team.whenOther",
          )}
        </span>
        <InfoTip
          label={t("Dashboard.Setup.moreAbout", {
            label: t("Dashboard.Setup.team.whenPerson"),
          })}
          text={t("Dashboard.Setup.team.whenTip")}
        />
      </div>

      {!advanced && (
        <>
          <div
            role="group"
            aria-label={t("Dashboard.Setup.team.presetsLabel")}
            className="mt-2 flex flex-wrap gap-2"
          >
            {PRESETS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={preset === value}
                onClick={() => choosePreset(value)}
                className={chipClass(preset === value)}
              >
                {t(`Dashboard.Setup.team.presets.${value}`)}
              </button>
            ))}
          </div>

          <div
            id={daysId}
            tabIndex={-1}
            role="group"
            aria-labelledby={`${daysId}-label`}
            aria-describedby={daysError ? `${daysId}-error` : undefined}
            // Seven 44px squares do not fit a 360px phone, so there the row is a
            // grid of seven equal columns, still 44px tall.
            className="mt-3 grid grid-cols-7 gap-1 rounded-lg focus:outline-none sm:flex sm:flex-wrap sm:gap-1.5"
          >
            {DAY_KEYS.map((day) => (
              <button
                key={day}
                type="button"
                aria-pressed={Boolean(workingDays[day])}
                aria-label={dayName(day)}
                onClick={() => toggleDay(day)}
                className={dayClass(Boolean(workingDays[day]))}
              >
                {t(`Dashboard.Setup.days.short.${day}`)}
              </button>
            ))}
          </div>
          {daysError && (
            <span id={`${daysId}-error`} className={formErrorTextClass}>
              {daysError}
            </span>
          )}

          <div className="mt-3 grid grid-cols-2 gap-3 sm:max-w-sm">
            <div className="min-w-0">
              <label
                htmlFor={`resource-start-${uid}`}
                className="mb-1.5 block text-xs font-semibold text-ink-600"
              >
                {t("Dashboard.Setup.team.from")}
              </label>
              <input
                type="time"
                id={`resource-start-${uid}`}
                value={resource.workingHours?.start || ""}
                onChange={(e) => setHours("start", e.target.value)}
                className={formControlClass}
              />
            </div>
            <div className="min-w-0">
              <label
                htmlFor={hoursId}
                className="mb-1.5 block text-xs font-semibold text-ink-600"
              >
                {t("Dashboard.Setup.team.to")}
              </label>
              <input
                type="time"
                id={hoursId}
                value={resource.workingHours?.end || ""}
                onChange={(e) => setHours("end", e.target.value)}
                className={hoursError ? formControlErrorClass : formControlClass}
                aria-invalid={hoursError ? "true" : undefined}
                aria-describedby={hoursError ? `${hoursId}-error` : undefined}
              />
            </div>
          </div>
          {hoursError && (
            <span id={`${hoursId}-error`} className={formErrorTextClass}>
              {hoursError}
            </span>
          )}
        </>
      )}

      <p
        className="m-0 mt-3 flex items-start gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800"
        data-testid="schedule-summary"
      >
        <i className="bi bi-calendar-week mt-0.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words">{describeSchedule(resource, t)}</span>
      </p>

      <div className="mt-2 rounded-lg border border-ink-200">
        {advanced ? (
          <p className="m-0 flex min-h-11 items-center gap-2 px-3 text-sm font-semibold text-ink-700">
            <i className="bi bi-clock-history" aria-hidden="true" />
            {t("Dashboard.Setup.team.perDay")}
          </p>
        ) : (
          <button
            type="button"
            aria-expanded={panelOpen}
            aria-controls={panelId}
            onClick={() => setPerDayOpen((open) => !open)}
            className="flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-lg px-3 text-left text-sm text-ink-700 transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600"
          >
            <i className="bi bi-clock-history" aria-hidden="true" />
            <span className="min-w-0 flex-1">{t("Dashboard.Setup.team.perDay")}</span>
            <i
              className={`bi bi-chevron-down text-xs transition-transform ${
                panelOpen ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </button>
        )}

        {panelOpen && (
          <div id={panelId} className="border-t border-ink-200 p-3">
            <ToggleSwitch
              id={`advanced-schedule-${uid}`}
              checked={advanced}
              onChange={(e) => setAdvanced(e.target.checked)}
              label={t("Dashboard.Setup.team.perDayToggle")}
            />

            {advanced && (
              <>
                <div
                  id={daysId}
                  tabIndex={-1}
                  className="mt-3 flex flex-col gap-2 focus:outline-none"
                >
                  {DAY_KEYS.map((day) => {
                    const schedule = resource.advancedSchedule?.[day] || {};
                    return (
                      <div
                        key={day}
                        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-ink-200 px-3 py-2"
                      >
                        <label
                          htmlFor={`${day}-enabled-${uid}`}
                          className="flex min-h-11 min-w-32 flex-1 cursor-pointer items-center gap-2 select-none"
                        >
                          <input
                            type="checkbox"
                            id={`${day}-enabled-${uid}`}
                            checked={Boolean(schedule.enabled)}
                            onChange={(e) => setDay(day, "enabled", e.target.checked)}
                            className="size-4 cursor-pointer accent-brand-600"
                          />
                          <span className="text-sm font-medium text-ink-900">
                            {dayName(day)}
                          </span>
                        </label>
                        {schedule.enabled && (
                          <div className="flex items-center gap-2">
                            <input
                              type="time"
                              aria-label={t("Dashboard.Wizard.weeklySchedule.dayStartTime", {
                                day: dayName(day),
                              })}
                              value={schedule.start || ""}
                              onChange={(e) => setDay(day, "start", e.target.value)}
                              className="w-28 rounded-lg border border-ink-200 bg-white px-2 py-2 text-sm text-ink-900 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 focus:outline-none"
                            />
                            <span className="text-ink-400" aria-hidden="true">
                              –
                            </span>
                            <input
                              type="time"
                              aria-label={t("Dashboard.Wizard.weeklySchedule.dayEndTime", {
                                day: dayName(day),
                              })}
                              value={schedule.end || ""}
                              onChange={(e) => setDay(day, "end", e.target.value)}
                              className="w-28 rounded-lg border border-ink-200 bg-white px-2 py-2 text-sm text-ink-900 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 focus:outline-none"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {daysError && (
                  <span className={formErrorTextClass}>{daysError}</span>
                )}
                {hoursError && (
                  <span id={hoursId} tabIndex={-1} className={formErrorTextClass}>
                    {hoursError}
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

AvailabilityEditor.propTypes = {
  resource: PropTypes.object.isRequired,
  /** The step's errors object; this editor reads its own keys from it. */
  errors: PropTypes.object,
  /** (field, value) — a field of the resource. */
  onUpdate: PropTypes.func.isRequired,
};

export default AvailabilityEditor;
